/**
 * The workstation's own voice for a language, or null when it has none.
 *
 * `undefined` while the browser is still populating its voice list — the distinction matters,
 * because "not yet known" and "this machine cannot speak Arabic" lead to different interfaces, and
 * conflating them is what makes a read-aloud button flicker in and out on load.
 */

import { useEffect, useState } from "react";

import { browserSpeechAvailable, loadVoices, pickVoice } from "./browserSpeech";

export function useBrowserVoice(
  language: string,
): SpeechSynthesisVoice | null | undefined {
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null | undefined>(
    browserSpeechAvailable() ? undefined : null,
  );

  useEffect(() => {
    if (!browserSpeechAvailable()) {
      setVoice(null);
      return;
    }

    let live = true;
    setVoice(undefined);
    void loadVoices().then((voices) => {
      if (live) setVoice(pickVoice(voices, language));
    });
    return () => {
      live = false;
    };
  }, [language]);

  return voice;
}
